import { objectWithoutProperties as _objectWithoutProperties, slicedToArray as _slicedToArray, extends as _extends, toConsumableArray as _toConsumableArray } from '../../_virtual/_rollupPluginBabelHelpers.js';
import * as React from 'react';
import { useEffect } from 'react';
import { Search, Check } from '@livingdesign/icons';
import '@walmart-web/livingdesign-components/index.esm.css';
import { Menu, MenuItem, SkeletonText } from '@walmart-web/livingdesign-components';
import '../Alert/Alert.js';
import { Button } from '../Button/Button.js';
import '../Button/ButtonGroup.js';
import '../Radio/Radio.js';
import '../Tag/Tag.js';
import { Checkbox } from '../Checkbox/Checkbox.js';
import '../Chip/Chip.js';
import classNames from '../../node_modules/classnames/index.js';
import { applyCommonProps } from '../utility/commonProps.js';
import '../IconButton/IconButton.js';
import '@livingdesign/tokens/dist/js/light/regular/components/Form';
import { TextField } from '../TextField/TextField.js';
import '../TabNavigation/TabNavigation.js';
import '../TabNavigation/TabNavigationItem.js';
import '../Modal/Modal.js';
import '@livingdesign/tokens/dist/js/light/regular/components/ErrorMessage';
import '../SearchBox/SearchBox.js';
import '../AutoComplete/AutoComplete.js';
import '../TextArea/TextArea.js';
import '../ProgressIndicator/ProgressIndicator.js';
import '../Panel/Panel.js';
import '../Calendar/locale/wmLocale.js';
import '../Calendar/locale/stdLocale.js';
import '../Calendar/context.js';
import 'dayjs';
import '../Calendar/RenderDays.js';
import '../Calendar/RenderWeeks.js';
import '../utility/constants.js';
import '@react-aria/ssr';
import '../CardActionArea/CardActionArea.js';
import '../HierarchySelector/HierarchySelector.js';
import '../Pagination/Pagination.js';
import '../DisplayTag/DisplayTag.js';
import '../PageHeader/PageHeader.js';
import '../Table/Table.js';
import styles from './MenuList.module.scss.js';
import '../Tooltip/Tooltip.js';
import '../ValueChange/ValueChange.js';

var _excluded = ["options", "preSelectedOptions", "menuTrigger", "isMulti", "menuTriggerRef", "menuPosition", "maxMenuHeight", "updateCheckedList", "setIsMenuOpen", "isLoading", "scrollableDivId", "isSearchEnabled", "searchOptions"];
/**
 * The MenuList component enables the user to select a single or multiple options from a given list.
 *
 * ### Content
 *
 * - Render the list of options by using the options prop. The options prop accepts array of strings, see example below.
 * - Set the list of pre-selected options using the preSelectedOptions prop, when isMulti is set to true, an array of multiple strings can be passed, but when isMulti is set to false, array with only one string value can be passed.
 * - Render trigger for the menu by using the menuTrigger prop and provide the ref to the menuTriggerRef prop for the trigger element.
 *
 * ```tsx
 * import * as React from "react";
 * import { MenuList, Button } from "@walmart-dataventures/shared-components";
 *
 * const options= ['jan','feb','mar','apr','may'];
 * const ref = React.useRef(null);
 * export default () => {
 *  return (
 *    <MenuList options={options} preSelectedOptions={['feb']} isMulti={false} menuTrigger={<Button ref={ref}>Menu Trigger</Button>} menuTriggerRef={ref} updateSelectedList={(checkedList) => {console.log('Selected options list', checkedList)}}/>
 *  )
 * }
 *
 * ```
 *
 * ### Menu Trigger and Trigger Ref
 *
 * The MenuList component provides menuTrigger prop, for menu to be triggered, we can provide single button-like element to it. And, menuTriggerRef must be provided the trigger element's ref to menuTriggerRef.
 * - ref also needs to be provided in the element that will be provided to the menuTrigger.
 *
 * ```tsx
 *
 * const ref = React.useRef(null);
 * const options = [{option1: true}, {option2: false}, {option3: false}];
 * <MenuList options={options} menuTrigger={<Button ref={ref}>Button</Button>} menuTriggerRef={ref} updateSelectedList={(checkedList) => {console.log('Selected options list', checkedList)}}/>
 * <MenuList options={options} menuTrigger={<IconButton ref={ref} a11yLabel='more'><More /></IconButton>} menuTriggerRef={ref} updateSelectedList={(checkedList) => {console.log('Selected options list', checkedList)}}/>
 * <MenuList options={options} menuTrigger={<Chip ref={ref}>Chip</Chip>}> menuTriggerRef={ref} updateSelectedList={(checkedList) => {console.log('Selected options list', checkedList)}}/>
 * ```
 *
 * ### Multi Select
 *
 * The MenuList component exposes an isMulti prop, to toggle between single and multi select. The isMulti prop defaults to single select.
 * - When the isMulti prop is set to false, only a single option can be selected. Post option selection, a checkmark is displayed next to the selected option.
 * - When the isMulti prop is set to true, multiple options can be selected.
 *
 * ```tsx
 *
 * const options = [{option1: true}, {option2: false}, {option3: false}];
 * <MenuList options={options} menuTrigger={<Button>Single Select</Button>} isMulti={false} preSelectedOptions={['feb']} updateSelectedList={(checkedList) => {console.log('Selected options list', checkedList)}}/> (default)
 * <MenuList options={options} menuTrigger={<Button>Multi Select</Button>} isMulti={true} preSelectedOptions={['jan','feb','mar]} updateSelectedList={(checkedList) => {console.log('Selected options list', checkedList)}}/>
 * ```
 *
 * ### Menu Position
 * The MenuList component provides menuPosition prop for menu to open in four different position:
 * - 'bottomLeft' (default)
 * - 'bottomRight'
 * - 'topLeft'
 * - 'topRight'
 *
 * ```tsx
 * <MenuList options={options} menuTrigger={<Button>Bottom Left</Button>} menuPosition='bottomLeft' updateSelectedList={(checkedList) => {console.log('Selected options list', checkedList)}}/> (default)
 * <MenuList options={options} menuTrigger={<Button>Bottom Right</Button>} menuPosition='bottomRight' updateSelectedList={(checkedList) => {console.log('Selected options list', checkedList)}}/>
 * <MenuList options={options} menuTrigger={<Button>Top Left</Button>} menuPosition='topLeft' updateSelectedList={(checkedList) => {console.log('Selected options list', checkedList)}}/>
 * <MenuList options={options} menuTrigger={<Button>Top Right</Button>} menuPosition='topRight' updateSelectedList={(checkedList) => {console.log('Selected options list', checkedList)}}/>
 * ```
 *
 * ### Menu Height
 * The MenuList components provides maxMenuHeight prop for menu to enable scrolling with max height provided to the prop.
 *
 * ```tsx
 * <MenuList options={options} maxMenuHeight='5rem' menuTrigger={<Button>Bottom Left</Button>} updateSelectedList={()=>{}}/>
 * ```
 *
 * ### Search Option
 *
 * Search can be enabled within the menu by setting `isSearchEnabled` to true
 * Is search is enabled, `searchOptions` should be passed to activate the state of the search bar.
 * Filtering logic should be handled on the parent component level
 *
 * ```tsx
 *    <MenuList options={options} isSearchEnabled={true} searchOptions={{ value: searchValue, setValue: setSearchValue }} menuTrigger={<Button ref={ref}>Menu Trigger</Button>} menuTriggerRef={ref} updateSelectedList={()=>{}}/>
 * ```
 */

var MenuList = function MenuList(props) {
  var _applyCommonProps = applyCommonProps(props, MenuList),
    options = _applyCommonProps.options,
    preSelectedOptions = _applyCommonProps.preSelectedOptions,
    menuTrigger = _applyCommonProps.menuTrigger,
    _applyCommonProps$isM = _applyCommonProps.isMulti,
    isMulti = _applyCommonProps$isM === void 0 ? false : _applyCommonProps$isM,
    menuTriggerRef = _applyCommonProps.menuTriggerRef,
    menuPosition = _applyCommonProps.menuPosition,
    maxMenuHeight = _applyCommonProps.maxMenuHeight,
    updateCheckedList = _applyCommonProps.updateCheckedList,
    setIsMenuOpen = _applyCommonProps.setIsMenuOpen,
    isLoading = _applyCommonProps.isLoading,
    _applyCommonProps$scr = _applyCommonProps.scrollableDivId,
    scrollableDivId = _applyCommonProps$scr === void 0 ? 'menuListScrollableDiv' : _applyCommonProps$scr,
    _applyCommonProps$isS = _applyCommonProps.isSearchEnabled,
    isSearchEnabled = _applyCommonProps$isS === void 0 ? false : _applyCommonProps$isS,
    searchOptions = _applyCommonProps.searchOptions,
    rest = _objectWithoutProperties(_applyCommonProps, _excluded);
  var _React$useState = React.useState(false),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    isOpen = _React$useState2[0],
    setIsOpen = _React$useState2[1];
  var _React$useState3 = React.useState(preSelectedOptions !== undefined ? preSelectedOptions : []),
    _React$useState4 = _slicedToArray(_React$useState3, 2),
    checkedList = _React$useState4[0],
    setCheckedList = _React$useState4[1];
  useEffect(function () {
    if (preSelectedOptions && (preSelectedOptions === null || preSelectedOptions === void 0 ? void 0 : preSelectedOptions.toString()) !== checkedList.toString()) setCheckedList(preSelectedOptions !== null && preSelectedOptions !== void 0 ? preSelectedOptions : []);
  }, [preSelectedOptions]);
  useEffect(function () {
    updateCheckedList(checkedList);
  }, [checkedList]);
  useEffect(function () {
    setIsMenuOpen === null || setIsMenuOpen === void 0 || setIsMenuOpen(isOpen);
  }, [isOpen]);
  var handleClick = function handleClick(option) {
    if (isMulti) {
      // when isMulti is true, if the option is already in the checkedList, we need to remove that option from the checkedList, else we add that option to checkedList.
      if (checkedList.includes(option)) {
        setCheckedList(checkedList.filter(function (item) {
          return item !== option;
        }));
      } else {
        setCheckedList([].concat(_toConsumableArray(checkedList), [option]));
      }
    } else {
      // when isMulti is false, if the option is already in the checkedList, we need to remove the option and return an empty array, else return an array with that option.
      if (!checkedList.includes(option)) {
        setCheckedList([option]);
      }
    }
  };
  var handleOnClose = function handleOnClose(event) {
    var target = event.target;
    if (target instanceof HTMLElement) {
      if (target.closest('[data-prevent-menu-close]')) {
        event.preventDefault();
        return;
      }
      if (target.nodeName === 'INPUT') {
        setIsOpen(true);
      } else {
        event.type === 'click' && isMulti ? setIsOpen(true) : setIsOpen(false);
      }
    }
  };
  return /*#__PURE__*/React.createElement(Menu, _extends({
    isOpen: isOpen,
    onClose: handleOnClose,
    onOpen: function onOpen() {
      return setIsOpen(true);
    },
    trigger: menuTrigger,
    triggerRef: menuTriggerRef,
    position: menuPosition
  }, rest), isSearchEnabled ? /*#__PURE__*/React.createElement("div", {
    className: styles.textBox
  }, /*#__PURE__*/React.createElement(TextField, {
    label: "",
    leadingIcon: /*#__PURE__*/React.createElement(Search, {
      size: "small"
    }),
    onChange: function onChange(event) {
      return searchOptions === null || searchOptions === void 0 ? void 0 : searchOptions.setValue(event.target.value);
    },
    size: "small",
    type: "text",
    placeholder: "Search",
    value: searchOptions === null || searchOptions === void 0 ? void 0 : searchOptions.value
  })) : null, /*#__PURE__*/React.createElement("div", {
    id: scrollableDivId,
    key: "menu",
    style: {
      maxHeight: maxMenuHeight !== undefined ? maxMenuHeight : window.innerHeight,
      overflowY: maxMenuHeight !== undefined ? 'scroll' : 'auto'
    },
    "data-testid": "menu"
  }, options.map(function (option, index) {
    return /*#__PURE__*/React.createElement(MenuItem, {
      key: index,
      UNSAFE_className: styles.menuItem
    }, typeof option === 'string' ? isMulti ? /*#__PURE__*/React.createElement(Checkbox, {
      label: option,
      checked: checkedList === null || checkedList === void 0 ? void 0 : checkedList.includes(option),
      onChange: function onChange() {
        return handleClick(option);
      }
    }) : /*#__PURE__*/React.createElement(React.Fragment, null, checkedList.includes(option) ? /*#__PURE__*/React.createElement(Check, null) : null, /*#__PURE__*/React.createElement(Button, {
      UNSAFE_className: classNames(styles.singleSelect, !checkedList.includes(option) ? styles.menuListOption : ''),
      onClick: function onClick() {
        return handleClick(option);
      }
    }, option)) : option // Render ReactNode directly if not a string
    );
  }), isLoading ? /*#__PURE__*/React.createElement("div", {
    className: classNames(styles.loaderWrapper)
  }, /*#__PURE__*/React.createElement(SkeletonText, {
    lines: 3,
    UNSAFE_className: styles.loader
  })) : null));
};

export { MenuList };
